<template>
  <VExpansionPanel data-test="passengersSelectionPanel">
    <ValidationObserver
      ref="passengersSelection"
      v-slot="{ invalid, validated }"
      name="passengersSelection"
    >
      <VExpansionPanelHeader
        v-blur
        class="body-1 d-flex align-items-center"
        disableIconRotate
        hideActions
      >
        <template #default="{ open }">
          <VRow class="d-flex align-items-center">
            <VIcon
              class="pr-2"
              :color="(invalid && validated) ? 'error' : ''"
            >
              mdi-account-multiple
            </VIcon>
            <template>
              <span v-if="adultsAmount !== 1">
                 {{ `${$t('bookingForm.adultsSummary', { adults: adultsAmount })},` }}
              </span>
              <span v-else>
                 {{ `${$t('bookingForm.adultSummary')},` }}
              </span>
              <span v-if="childrenAmount !== 1" class="ml-1">
                 {{ $t('bookingForm.childrenSummary', { children: childrenAmount }) }}
              </span>
              <span v-else class="ml-1">
                 {{ $t('bookingForm.childSummary') }}
              </span>
            </template>
            <VSpacer />
            <VIcon :class="open ? 'arrow-rotate' : ''">
              {{ 'mdi-chevron-down' }}
            </VIcon>
          </VRow>
        </template>
      </VExpansionPanelHeader>
      <VExpansionPanelContent
        ref="passengerPanelContent"
        v-if="maxStandardSeatCapacity"
        data-test="passengersSelectionPanelContent"
        eager
      >
        <PassengerItem
          v-for="(item, index) in getItems"
          :key="index"
          :refName="item.refName"
          :dataTest="item.dataTest"
          :maxStandardSeatCapacity="item.maxStandardSeatCapacity"
          :wheelchairPlacesAmount="item.wheelchairPlacesAmount"
          :amount="item.amount"
          :maxValue="item.maxValue"
          :adultsAmount="item.adultsAmount"
          :passengersAmount="item.passengersAmount"
          :childrenAmount="item.childrenAmount"
          :icon="item.icon"
          :nameKey="item.nameKey"
          :isBookingFormNarrow="item.isBookingFormNarrow"
          :className="item.className"
          :updateAmountCallback="item.updateAmountCallback"
        />
        <div
          v-if="invalid"
          data-test="maximumSeatsAreReachedError"
          class="mb-2"
        >
          <span class="caption error--text">
            {{ $t(getPassengersError(validated), {
              passengers: getPassengersAmount - wheelchairPlacesAmount,
              maxPassengers: getVehicleCategory.maxStandardSeatCapacity
            }) }} </span>
          <span
            v-if="getPassengersError(validated) === 'bookingForm.validations.maxLimitForStandardSeats' && wheelchairPlacesAmount <= getPassengersAmount"
            class="caption"
          >{{ $t('bookingForm.hints.maxLimitForSpecialSeatsCapacity', {
              specialSeats: wheelchairPlacesAmount,
              maxSpecialSeats: getVehicleCategory.maxSpecialSeatCapacity
            }) }}
          </span>
        </div>
        <div
          v-if="getPassengersError(validated) !== 'bookingForm.validations.maxLimitForStandardSeats' && wheelchairPlacesAmount <= getPassengersAmount"
          data-test="maximumSeatsAreReachedHint"
          class="mb-2"
        >
          <span class="caption textTertiary--text">
            {{ getHintMessage }}
          </span>
        </div>
      </VExpansionPanelContent>
    </ValidationObserver>
  </VExpansionPanel>
</template>
<script lang="ts">
export default {
  name: 'SPassengersSelection',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useI18n, useVuetify } from '../../../composables';
import PassengerItem from './PassengerItem.vue';

const props = defineProps({
  adultsAmount: {
    type: Number,
    default: null,
  },
  childrenAmount: {
    type: Number,
    default: null,
  },
  wheelchairPlacesAmount: {
    type: Number,
    default: null,
  },
  vehicleCategoryId: {
    type: String,
    default: null,
  },
  vehicleCategories: {
    type: Array,
    default: () => [],
  },
  appConfig: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['updateAdults', 'updateChildren']);

const { t } = useI18n();
const vuetify = useVuetify();
const passengersSelection = ref(null);

const getVehicleCategory = computed(() => props.vehicleCategories.find((vehicleCategory) => vehicleCategory.vehicleCategoryId === props.vehicleCategoryId));

const maxStandardSeatCapacity = computed(() => getVehicleCategory.value?.maxStandardSeatCapacity + props.wheelchairPlacesAmount);

const isMaxSeatsAreReached = computed(() => props.adultsAmount + props.childrenAmount === maxStandardSeatCapacity.value);

const maxAdults = computed(() => maxStandardSeatCapacity.value - props.childrenAmount);

const maxChildren = computed(() => maxStandardSeatCapacity.value - props.adultsAmount);

const getPassengersAmount = computed(() => props.childrenAmount + props.adultsAmount);

const isBookingFormNarrow = computed(() => vuetify.breakpoint.smAndDown);

const getHintMessage = computed(() => {
  const hint = t('bookingForm.hints.maxLimitForStandardSeats', {
    passengers: getPassengersAmount.value - props.wheelchairPlacesAmount,
    maxPassengers: getVehicleCategory.value.maxStandardSeatCapacity,
  });

  return getVehicleCategory.value.maxSpecialSeatCapacity
    ? hint.concat(', ', t('bookingForm.hints.maxLimitForSpecialSeatsCapacity', {
      specialSeats: props.wheelchairPlacesAmount,
      maxSpecialSeats: getVehicleCategory.value.maxSpecialSeatCapacity
    }) )
    : hint;
});

function updateAdults(newValue) {
  emit('updateAdults', newValue);
  nextTick(() => {
    passengersSelection.value.validate();
  });
}

function updateChildren(newValue) {
  emit('updateChildren', newValue);
  nextTick(() => {
    passengersSelection.value.validate();
  });
}

function getPassengersError() {
  const passengersSelectionObserver = passengersSelection.value;
  return passengersSelectionObserver?.errors?.counter
    ?.find((message) => message === 'bookingForm.validations.maxLimitForStandardSeats'
    || message === 'bookingForm.validations.noPassengers'
    || message === 'bookingForm.validations.wheelchairPlacesDoesntCompareWithPassengers');
}

const getItems = computed(() => [
  {
    refName: 'adultsCounter',
    dataTest: 'Adults',
    maxStandardSeatCapacity: maxStandardSeatCapacity.value,
    wheelchairPlacesAmount: props.wheelchairPlacesAmount,
    maxValue: maxAdults.value,
    amount: props.adultsAmount,
    adultsAmount: props.adultsAmount,
    passengersAmount: getPassengersAmount.value,
    childrenAmount: props.childrenAmount,
    icon: 'mdi-account',
    nameKey: 'bookingForm.adults',
    isBookingFormNarrow: isBookingFormNarrow.value,
    className: props.appConfig.isNightCardsSupport ? 'pb-4' : '',
    updateAmountCallback: updateAdults,
  },
  {
    refName: 'childrenCounter',
    dataTest: 'Children',
    maxStandardSeatCapacity: maxStandardSeatCapacity.value,
    wheelchairPlacesAmount: props.wheelchairPlacesAmount,
    maxValue: maxChildren.value,
    amount: props.childrenAmount,
    adultsAmount: props.adultsAmount,
    passengersAmount: getPassengersAmount.value,
    childrenAmount: props.childrenAmount,
    icon: 'mdi-face-man',
    nameKey: 'bookingForm.children',
    isBookingFormNarrow: isBookingFormNarrow.value,
    className: '',
    updateAmountCallback: updateChildren,
  },
]);
</script>

<style scoped lang="scss">
#test > * {
  padding-top: 0px;
  padding-right: 0px;
  padding-bottom: 0px;
  padding-left: 50px;
}

.v-expansion-panel-content__wrap {
  padding-bottom: unset !important;
}
</style>
